import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { TrendingDown, Calendar, DollarSign, Package } from 'lucide-react';
import './WastageTab.css';

const WastageTab = ({ hotelId, token }) => {
  const [wastageData, setWastageData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  const [error, setError] = useState('');

  useEffect(() => {
    if (hotelId && token) {
      fetchWastageReport();
    }
  }, [hotelId, token, dateRange]);

  const fetchWastageReport = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/inventory/reports/wastage?hotel_id=${hotelId}&start_date=${dateRange.start}&end_date=${dateRange.end}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const rawRows = Array.isArray(response.data?.data?.wastage_details)
        ? response.data.data.wastage_details
        : Array.isArray(response.data?.data)
          ? response.data.data
          : Array.isArray(response.data)
            ? response.data
            : [];

      const normalizedRows = rawRows.map((row) => ({
        ...row,
        total_wastage_quantity: row.total_wastage_quantity ?? row.total_quantity ?? 0,
        total_wastage_value: row.total_wastage_value ?? row.total_value ?? 0,
        unit_of_measurement: row.unit_of_measurement ?? row.unit ?? '-'
      }));

      setWastageData(normalizedRows);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching wastage report:', err);
      setError('Failed to load wastage report');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="tab-loading">
        <div className="loading-spinner"></div>
        <p>Loading wastage data...</p>
      </div>
    );
  }

  const safeWastageData = Array.isArray(wastageData) ? wastageData : [];

  const totalWastageCost = safeWastageData.reduce((sum, item) => 
    sum + (parseFloat(item.total_wastage_value) || 0), 0
  );

  const totalWastageQuantity = safeWastageData.reduce((sum, item) => 
    sum + (parseFloat(item.total_wastage_quantity) || 0), 0
  );

  return (
    <div className="wastage-tab">
      <div className="tab-header">
        <h2><TrendingDown size={24} /> Wastage Analysis</h2>
        <div className="date-range-selector">
          <Calendar size={20} />
          <input
            type="date"
            value={dateRange.start}
            onChange={(e) => setDateRange({...dateRange, start: e.target.value})}
          />
          <span>to</span>
          <input
            type="date"
            value={dateRange.end}
            onChange={(e) => setDateRange({...dateRange, end: e.target.value})}
          />
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="wastage-summary">
        <div className="summary-card">
          <div className="summary-icon">
            <DollarSign size={32} />
          </div>
          <div className="summary-content">
            <h4>Total Wastage Cost</h4>
            <p className="summary-value">${totalWastageCost.toFixed(2)}</p>
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-icon">
            <Package size={32} />
          </div>
          <div className="summary-content">
            <h4>Total Items Wasted</h4>
            <p className="summary-value">{safeWastageData.length}</p>
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-icon">
            <TrendingDown size={32} />
          </div>
          <div className="summary-content">
            <h4>Total Wastage Incidents</h4>
            <p className="summary-value">{safeWastageData.reduce((sum, item) => sum + (parseInt(item.wastage_count, 10) || 0), 0)}</p>
          </div>
        </div>
      </div>

      <div className="wastage-table-container">
        <table className="wastage-table">
          <thead>
            <tr>
              <th>Item Name</th>
              <th>Category</th>
              <th>Total Quantity Wasted</th>
              <th>Unit</th>
              <th>Wastage Count</th>
              <th>Total Value</th>
              <th>% of Total</th>
            </tr>
          </thead>
          <tbody>
            {safeWastageData.map((item, idx) => {
              const percentage = totalWastageCost > 0 
                ? ((parseFloat(item.total_wastage_value) / totalWastageCost) * 100).toFixed(1) 
                : 0;
              
              return (
                <tr key={idx}>
                  <td><strong>{item.item_name}</strong></td>
                  <td>{item.category_name}</td>
                  <td>{parseFloat(item.total_wastage_quantity).toFixed(2)}</td>
                  <td>{item.unit_of_measurement}</td>
                  <td>{item.wastage_count}</td>
                  <td><strong>${parseFloat(item.total_wastage_value).toFixed(2)}</strong></td>
                  <td>
                    <div className="percentage-bar">
                      <div className="percentage-fill" style={{width: `${percentage}%`}}></div>
                      <span className="percentage-text">{percentage}%</span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {safeWastageData.length === 0 && (
        <div className="empty-state">
          <TrendingDown size={48} />
          <h3>No Wastage Data</h3>
          <p>No wastage recorded in the selected date range</p>
        </div>
      )}
    </div>
  );
};

export default WastageTab;
